import { memo, useState } from "react";
import { Table as BTable } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { TableCell } from "@/components/Elements/TableCell";
import Filter from "./Filter";

const getCommonPinningStyles = (column) => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column?.getIsPinned("right");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column?.getIsPinned("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-4px 0 4px -4px gray inset"
      : isFirstRightPinnedColumn
      ? "4px 0 4px -4px gray inset"
      : undefined,
    left: isPinned === "left" ? `0px` : undefined,
    right: isPinned === "right" ? `0px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

const EventsTable = ({
  columns,
  tableEvents,
  editingId,
  setEditingEventId,
  previewEventId,
  setPreviewEventId,
  deleteEvent,
  updateData,
  updateEvent,
  showSubEventsList,
  setSubEventsListData
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnPinning, setColumnPinning] = useState({
    left: [],
    right: ["Actions"],
  });

  const table = useReactTable({
    data: tableEvents,
    columns,
    state: {
      columnFilters,
      columnPinning,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      columnPinning: {
        left: [],
        right: ["Actions"],
      },
    },
    meta: {
      editingId,
      setEditingEventId,
      previewEventId,
      setPreviewEventId,
      deleteEvent,
      updateData,
      updateEvent,
      setColumnPinning,
      showSubEventsList,
      setSubEventsListData,
    },
    filterFns: {
      idFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.id).includes(filterValue);
      },
      activePlayerFilterFn: (row, columnId, filterValue, addMeta) => {
        return (
          String(row.original.super_track.super_track_uid) ===
          String(filterValue)
        );
      },
      passivePlayerFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.extra.player) === String(filterValue);
      },
      resultFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.success) === String(filterValue);
      },
      kindFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.super_track.kind) === String(filterValue);
      },
      startTimeFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.timestamp).includes(String(filterValue));
      },
      endTimeFilterFn: (row, columnId, filterValue, addMeta) => {
        return String(row.original.extra.event_end).includes(
          String(filterValue)
        );
      },
      subEventsFilterFn: (row, columnId, filterValue, addMeta) => {
        return Object.keys(row.original.extra.additional_events).includes(
          filterValue
        );
      },
    },
  });

  return (
    <BTable hover size="sm">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                style={{ ...getCommonPinningStyles(header.column) }}
              >
                {header.isPlaceholder ? null : (
                  <>
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <div>
                        <Filter column={header.column} />
                      </div>
                    ) : null}
                  </>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                style={{
                  backgroundColor: row?.original?.highlighted
                    ? "#9DDC18"
                    : "white",
                  ...getCommonPinningStyles(cell.column),
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </tr>
        ))}
      </tbody>
    </BTable>
  );
};

export default memo(EventsTable);
